import React, { useState } from 'react';
import logo from '../../assets/logo-lockup.png';
import LegalFooterLinks from '../Legal/LegalFooterLinks';
import AuthHomeLink from './AuthHomeLink';
import './AuthPage.css';

/**
 * LoginPage: Email + password login form.
 * Calls onLogin(email, password) on submit.
 * Calls onSwitchToRegister() when user clicks "Create account".
 * Calls onForgotPassword() when user clicks "Forgot password?".
 */
const LoginPage = ({ onLogin, onSwitchToRegister, onForgotPassword, onStartListing, onHome, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!email.trim() || !password) {
      setLocalError('Please enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      await onLogin(email.trim(), password);
    } catch (err) {
      setLocalError(err.message || 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <AuthHomeLink onHome={onHome} />
        <div className="auth-brand auth-brand--logo">
          <img src={logo} alt="cutthe6.com" className="auth-brand-logo" />
          <p className="auth-brand-tagline">Sell your home. Keep more of your money.</p>
        </div>

        <h1 className="auth-title">Sign in</h1>
        <p className="auth-subtitle">Welcome back — sign in to continue</p>

        {displayError && (
          <div className="auth-error" id="login-error" role="alert">
            {displayError}
          </div>
        )}

        <form
          className="auth-form"
          onSubmit={handleSubmit}
          noValidate
          {...(displayError ? { 'aria-describedby': 'login-error' } : {})}
        >
          <div className="auth-field">
            <label htmlFor="login-email" className="auth-label">Email</label>
            <input
              id="login-email"
              type="email"
              className="auth-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              disabled={submitting}
            />
          </div>

          <div className="auth-field">
            <label htmlFor="login-password" className="auth-label">Password</label>
            <input
              id="login-password"
              type="password"
              className="auth-input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          {onForgotPassword && (
            <p className="auth-forgot-row">
              <button
                type="button"
                className="auth-link-btn"
                onClick={onForgotPassword}
                disabled={submitting}
              >
                Forgot password?
              </button>
            </p>
          )}
        </form>

        <p className="auth-switch">
          No account?{' '}
          <button
            type="button"
            className="auth-link-btn"
            onClick={onSwitchToRegister}
            disabled={submitting}
          >
            Sign up
          </button>
        </p>
        {onStartListing && (
          <p className="auth-switch">
            Selling a home?{' '}
            <button
              type="button"
              className="auth-link-btn"
              onClick={onStartListing}
              disabled={submitting}
            >
              Start your listing
            </button>
          </p>
        )}

        <LegalFooterLinks className="auth-legal-links" />
      </div>
    </div>
  );
};

export default LoginPage;
